import { Dispatch, ReactElement, SetStateAction } from "react";

import { SelectData } from "../../models";
import { GetWorkflowTemplateResponse } from "../../service/query";
import {
  ActionButtons,
  Column,
  DatePicker,
  Divider,
  ErrorBox,
  FlyoutPanel,
  KanaTooltip,
  ReadonlyText,
  Row,
  Select,
  TextInput,
} from "../../widget";
import { DealStagesSection } from "./components";
import { useWorkflowTemplatePanel } from "./useWorkflowTemplatePanel";

interface WorkflowTemplatePanelProps {
  workflowTemplateUuid?: string;
  workflowTemplate?: GetWorkflowTemplateResponse;
  workflowTemplateType?: string;
  projectTypesSelectData: SelectData | undefined;
  show: boolean;
  onClose: () => void;
  setRefresh: Dispatch<SetStateAction<boolean>>;
}

export const WorkflowTemplatePanel = ({
  workflowTemplateUuid,
  workflowTemplate,
  workflowTemplateType,
  projectTypesSelectData,
  show,
  onClose,
  setRefresh,
}: WorkflowTemplatePanelProps): JSX.Element => {
  const {
    errors,
    isHandleSubmitLoading,
    isUpdate,

    currentTemplate,
    dealStages,
    canAddDealStage,
    canRemoveDealStage,
    canReorderDealStages,
    isDealStageReorderMode,
    setCurrentTemplate,

    handleAddDealStage,
    handleAddDealStageKeyDate,
    handleCancelDealStageReorder,
    handleClosePanel,
    handleEnterDealStageReorderMode,
    handleRemoveDealStage,
    handleRemoveDealStageKeyDate,
    handleReorderDealStageDrop,
    handleSaveDealStageReorder,
    handleSubmit,
    handleUpdateDealStageDescription,
    handleUpdateDealStageKeyDate,
    handleUpdateDealStageName,
  } = useWorkflowTemplatePanel({
    workflowTemplateUuid,
    workflowTemplate,
    workflowTemplateType,
    show,

    onClose,
    setRefresh,
  });
  const isDealWorkflowTemplate = workflowTemplateType === "Deal";
  const shouldShowProjectType = !isDealWorkflowTemplate;

  const content = (): ReactElement => {
    return (
      <div className="WorkflowTemplatePanelContent">
        <form onSubmit={(e) => handleSubmit(e)}>
          <Row spacingV="xl">
            <Column span={12}>
              <h1>{isUpdate ? "Edit workflow template" : "Create new workflow template"}</h1>
            </Column>
          </Row>
          <Row spacingV="xl">
            <Column span={12}>
              <p className="body1Light">
                A workflow template defines the steps and tasks required to progress a project through its lifecycle.
                When a template is applied to a project, it automatically generates tasks based on the predefined
                structure, ensuring consistency and compliance with required processes.
              </p>
            </Column>
          </Row>
          {errors && errors.length > 0 && (
            <Row spacingV="ml">
              <Column span={12}>
                <ErrorBox text={errors.map((error) => error.message)} />
              </Column>
            </Row>
          )}
          <Row spacingV="l">
            <Column span="4_half">
              <TextInput
                label="Template name*"
                name="Template name"
                value={currentTemplate?.name ?? workflowTemplate?.name}
                onChange={(value) => setCurrentTemplate({ ...currentTemplate, name: value })}
                maxLength={100}
              />
            </Column>
            <Column offset="1_half" span="4_half">
              <TextInput
                label="Template description*"
                name="Template description"
                value={currentTemplate?.description ?? workflowTemplate?.description}
                onChange={(value) => setCurrentTemplate({ ...currentTemplate, description: value })}
                maxLength={500}
              />
            </Column>
          </Row>
          <Row spacingV="xl">
            {shouldShowProjectType && (
              <Column span="4_half">
                {workflowTemplate?.projectType?.name ? (
                  <ReadonlyText label="Project type" value={workflowTemplate.projectType.name} />
                ) : (
                  <Select
                    label="Project type*"
                    data={projectTypesSelectData ?? []}
                    value={currentTemplate?.projectTypeUuid}
                    onChange={(value) => setCurrentTemplate({ ...currentTemplate, projectTypeUuid: value })}
                  />
                )}
              </Column>
            )}
            <Column offset={shouldShowProjectType ? "1_half" : undefined} span="4_half">
              <DatePicker
                label="Expiration date"
                font="figtree"
                tooltip={
                  <KanaTooltip
                    tooltipHeader=""
                    tooltipText="
                    <p>Once applied, this workflow will expire on the selected date. You will no longer be able to edit or apply this workflow to projects.</p>
                    <p>If there is no expiration date, leave this field blank.</p>"
                    allowDangerousHtml
                  />
                }
                value={currentTemplate.validTo ?? workflowTemplate?.validTo ?? undefined}
                onChange={(value) => setCurrentTemplate({ ...currentTemplate, validTo: value ?? null })}
              />
            </Column>
          </Row>
          <Row spacingV="xl">
            <Column span={12}>
              <Divider type="horizontal" />
            </Column>
          </Row>
          {isDealWorkflowTemplate && (
            <>
              <Row spacingV="xl">
                <Column span={12}>
                  <DealStagesSection
                    canAddStage={canAddDealStage}
                    canRemoveStage={canRemoveDealStage}
                    canReorderStages={canReorderDealStages}
                    dealStages={dealStages}
                    hideDefaultKeyDates
                    isReorderMode={isDealStageReorderMode}
                    onAddKeyDate={handleAddDealStageKeyDate}
                    onAddStage={handleAddDealStage}
                    onCancelReorder={handleCancelDealStageReorder}
                    onEnterReorderMode={handleEnterDealStageReorderMode}
                    onRemoveKeyDate={handleRemoveDealStageKeyDate}
                    onRemoveStage={handleRemoveDealStage}
                    onReorderStageDrop={handleReorderDealStageDrop}
                    onSaveReorder={handleSaveDealStageReorder}
                    onUpdateKeyDate={handleUpdateDealStageKeyDate}
                    onUpdateStageDescription={handleUpdateDealStageDescription}
                    onUpdateStageName={handleUpdateDealStageName}
                  />
                </Column>
              </Row>
              <Row spacingV="xl">
                <Column span={12}>
                  <Divider type="horizontal" />
                </Column>
              </Row>
            </>
          )}
          <Row>
            <Column span={12}>
              <ActionButtons
                submitButtonLabel={isUpdate ? "Update" : "Create"}
                onCancel={handleClosePanel}
                isLoading={isHandleSubmitLoading}
              />
            </Column>
          </Row>
        </form>
      </div>
    );
  };

  return (
    <FlyoutPanel
      show={show}
      onClose={handleClosePanel}
      size="Large"
      contentChildren={content()}
      header=""
      buttonsChildren={undefined}
      height="Full"
    />
  );
};
